/**
 * Generic Proxy handler for RPCs that are accepted before device state catches up.
 *
 * BLE / viaq / cover moves often return `{ isok: true }` as soon as the command is
 * queued. The UI still needs a live reading to confirm. This handler:
 * 1. Runs the original request.
 * 2. If it fails, resolves `rpc_failed`.
 * 3. If it succeeds, waits until `observeLive` reports a settled value.
 * 4. Gives up with `stalled` (no progress) or `timeout` (hard cap).
 * 5. A newer call, or `abort()`, resolves the previous wait as `aborted`.
 *
 * @see docs/helpers/settling-request-proxy.md
 */

export const DEFAULT_SETTLE_TIMEOUT_MS = 120_000;
export const DEFAULT_STALL_MS = 30_000;

export type SettlingOutcome = 'settled' | 'rpc_failed' | 'timeout' | 'stalled' | 'aborted';

export type SettlingRequestResult<TRes> = {
	response?: TRes;
	outcome: SettlingOutcome;
};

export type SettlingTimers = {
	now: () => number;
	setTimeout: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
	clearTimeout: (id: ReturnType<typeof setTimeout>) => void;
};

export type SettlingRequestHandlerOptions<TArgs extends unknown[], TLive, TExpected, TRes> = {
	getExpected: (...args: TArgs) => TExpected;
	isSettled: (live: TLive, expected: TExpected) => boolean;
	isProgress?: (previous: TLive | undefined, next: TLive, expected: TExpected) => boolean;
	isOk?: (response: TRes) => boolean;
	timeoutMs?: number;
	stallMs?: number;
	timers?: Partial<SettlingTimers>;
	/** Fires when the RPC is accepted and the handler starts waiting for live state. */
	onAccepted?: (response: TRes) => void;
	/**
	 * Viaq / BLE often returns a non-ok envelope even though the command was queued.
	 * Keep waiting for live state instead of resolving `rpc_failed` immediately.
	 */
	waitAfterRpcFailure?: boolean;
};

type Inflight<TRes, TExpected, TLive> = {
	expected: TExpected;
	response?: TRes;
	done: boolean;
	lastProgressLive?: TLive;
	timeoutId?: ReturnType<typeof setTimeout>;
	stallId?: ReturnType<typeof setTimeout>;
	resolve: (result: SettlingRequestResult<TRes>) => void;
};

export type SettlingRequestHandler<
	TFn extends (...args: never[]) => Promise<unknown>,
	TLive,
	TExpected,
> = ProxyHandler<TFn> & {
	observeLive: (live: TLive) => void;
	abort: () => void;
	getPending: () => TExpected | undefined;
};

export type SettlingRequestProxy<TArgs extends unknown[], TRes, TLive, TExpected> = ((
	...args: TArgs
) => Promise<SettlingRequestResult<TRes>>) & {
	observeLive: (live: TLive) => void;
	abort: () => void;
	getPending: () => TExpected | undefined;
};

const defaultIsOk = (response: { isok?: boolean } | undefined): boolean => Boolean(response?.isok);

const resolveTimers = (timers?: Partial<SettlingTimers>): SettlingTimers => ({
	now: timers?.now ?? Date.now,
	setTimeout: timers?.setTimeout ?? setTimeout,
	clearTimeout: timers?.clearTimeout ?? clearTimeout,
});

/**
 * Builds a {@link ProxyHandler} that wraps an async request with settle / stall / timeout.
 * Feed live readings through `observeLive` after each status update.
 */
export const createSettlingRequestHandler = <
	TFn extends (...args: never[]) => Promise<{ isok?: boolean }>,
	TLive,
	TExpected,
>(
	options: SettlingRequestHandlerOptions<Parameters<TFn>, TLive, TExpected, Awaited<ReturnType<TFn>>>,
): SettlingRequestHandler<TFn, TLive, TExpected> => {
	type TArgs = Parameters<TFn>;
	type TRes = Awaited<ReturnType<TFn>>;

	const timers = resolveTimers(options.timers);
	const timeoutMs = options.timeoutMs ?? DEFAULT_SETTLE_TIMEOUT_MS;
	const stallMs = options.stallMs ?? DEFAULT_STALL_MS;
	const isOk = options.isOk ?? defaultIsOk;
	const isProgress = options.isProgress;

	let lastLive: TLive | undefined;
	let inflight: Inflight<TRes, TExpected, TLive> | undefined;

	const clearTimers = (session: Inflight<TRes, TExpected, TLive>) => {
		if (session.timeoutId !== undefined) timers.clearTimeout(session.timeoutId);
		if (session.stallId !== undefined) timers.clearTimeout(session.stallId);
		session.timeoutId = undefined;
		session.stallId = undefined;
	};

	const complete = (
		session: Inflight<TRes, TExpected, TLive>,
		result: SettlingRequestResult<TRes>,
	) => {
		if (session.done) return;
		session.done = true;
		clearTimers(session);
		if (inflight === session) inflight = undefined;
		session.resolve(result);
	};

	const armStall = (session: Inflight<TRes, TExpected, TLive>) => {
		if (stallMs <= 0) return;
		if (session.stallId !== undefined) timers.clearTimeout(session.stallId);
		session.stallId = timers.setTimeout(() => {
			complete(session, { response: session.response, outcome: 'stalled' });
		}, stallMs);
	};

	const armTimeout = (session: Inflight<TRes, TExpected, TLive>) => {
		if (timeoutMs <= 0) return;
		session.timeoutId = timers.setTimeout(() => {
			complete(session, { response: session.response, outcome: 'timeout' });
		}, timeoutMs);
	};

	const observeLive = (live: TLive) => {
		const previous = inflight?.lastProgressLive ?? lastLive;
		lastLive = live;
		if (!inflight || inflight.done) return;
		if (options.isSettled(live, inflight.expected)) {
			complete(inflight, { response: inflight.response, outcome: 'settled' });
			return;
		}
		const progressed = isProgress
			? isProgress(previous, live, inflight.expected)
			: previous !== live;
		if (progressed) {
			inflight.lastProgressLive = live;
			armStall(inflight);
		}
	};

	const abort = () => {
		if (!inflight) return;
		complete(inflight, { response: inflight.response, outcome: 'aborted' });
	};

	const handler: SettlingRequestHandler<TFn, TLive, TExpected> = {
		observeLive,
		abort,
		getPending: () => inflight?.expected,
		apply(target, thisArg, argArray) {
			abort();
			const args = argArray as TArgs;
			const expected = options.getExpected(...args);

			return new Promise<SettlingRequestResult<TRes>>((resolve) => {
				const session: Inflight<TRes, TExpected, TLive> = {
					expected,
					done: false,
					lastProgressLive: lastLive,
					resolve,
				};
				inflight = session;

				Promise.resolve(Reflect.apply(target, thisArg, args) as Promise<TRes>)
					.then((response) => {
						if (session.done) return;
						session.response = response;
						if (!isOk(response) && !options.waitAfterRpcFailure) {
							complete(session, { response, outcome: 'rpc_failed' });
							return;
						}
						if (lastLive !== undefined && options.isSettled(lastLive, expected)) {
							complete(session, { response, outcome: 'settled' });
							return;
						}
						if (isOk(response)) {
							options.onAccepted?.(response);
						}
						armTimeout(session);
						armStall(session);
					})
					.catch(() => {
						if (session.done) return;
						complete(session, { outcome: 'rpc_failed' });
					});
			});
		},
	};

	return handler;
};

/**
 * Wraps `request` with {@link createSettlingRequestHandler}.
 * The returned function resolves a {@link SettlingRequestResult} instead of the raw RPC payload.
 */
export const createSettlingRequestProxy = <
	TArgs extends unknown[],
	TRes extends { isok?: boolean },
	TLive,
	TExpected,
>(
	request: (...args: TArgs) => Promise<TRes>,
	options: SettlingRequestHandlerOptions<TArgs, TLive, TExpected, TRes>,
): SettlingRequestProxy<TArgs, TRes, TLive, TExpected> => {
	const handler = createSettlingRequestHandler<typeof request, TLive, TExpected>(
		options as SettlingRequestHandlerOptions<Parameters<typeof request>, TLive, TExpected, TRes>,
	);

	return new Proxy(request, {
		apply: (target, thisArg, argArray) => handler.apply?.(target, thisArg, argArray),
		get(target, prop, receiver) {
			if (prop === 'observeLive') return handler.observeLive;
			if (prop === 'abort') return handler.abort;
			if (prop === 'getPending') return handler.getPending;
			const value = Reflect.get(target, prop, receiver);
			return typeof value === 'function' ? value.bind(target) : value;
		},
	}) as SettlingRequestProxy<TArgs, TRes, TLive, TExpected>;
};
